import React, { useState, useEffect, useMemo } from 'react';
import Select from 'react-select';
import { AsyncPaginate } from 'react-select-async-paginate';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Modal from 'react-bootstrap/Modal';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { ClipLoader } from 'react-spinners';
import LeaseApiService from '../../services/apiService';
import { getName } from '../../../Redux/selecters';
import { useCurrency } from '../../../INVENTORY/Layouts/currencyContext';

// PM Unit Detail popup: edits the bank / PM fields of an existing unit.
// Loads via GetUnitDetailsByRefNo and saves through UnitManagement/Update by sending the
// loaded record back with just these fields overridden, so every other unit field is preserved.

const LEASE_CLASS_OPTIONS = ['Sale', 'Lease', 'PM'].map((c) => ({ value: c, label: c }));

const emptyForm = {
  refNo: '',
  owner: null,
  unitClass: null,
  rentPerMonth: '',
  maintFee: '',
  dueDate: null,
  accountName: '',
  iban: '',
  accountNo: '',
  bankName: null,
  bankAddress: '',
  swiftCode: '',
};

const selectStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const BOOLEAN_KEYS = [
  'adv_Listing',
  'adv_NewsPaper',
  'adv_Web',
  'adv_OpenHouse',
  'adv1',
  'adv2',
  'adv3',
  'negotiable',
  'ownerOccupied',
  'leased',
  'mortgaged',
  'propertyRegistered',
  'publishStatus',
];

const DATE_KEYS = ['completion_Date', 'leaseExpiry', 'effeciency', 'refered_On', 'createdOn', 'modifiedOn', 'publishDate'];

// Field types the Update API expects (mirrors the body built by updateUnitMaster.js). The loaded
// record can return them as other JSON types (e.g. a number where a string is required).
const STRING_KEYS = new Set(
  [
    'ref_No', 'property_No', 'property_Name', 'unit_No', 'rerA_STR_No', 'street_No', 'street_Name', 'unit_Type', 'unit_Category',
    'unit_Style', 'unit_Model', 'unit_Zone', 'plot_No', 'plot_Size', 'block', 'floor', 'unit_View', 'unit_Class', 'livingRooms',
    'bedrooms', 'storeRooms', 'parking', 'facilities', 'status', 'payment_Plan', 'broker_Name', 'seller_Id', 'seller_Name',
    'adv_Remarks1', 'adv_Remarks2', 'adv_Remarks3', 'remarks', 'internalRemarks', 'otherSpace1', 'bathrooms', 'balcony', 'terrace',
    'other_Fees_Remarks', 'short_Term_Period', 'currency', 'rentPayableTo', 'rentBankName', 'rentBankAcNo', 'secDepPayableTo',
    'comPayableTo', 'key_Status', 'serviceCharge', 'mandate', 'financeCompany', 'mortgageCompany', 'portfolioManager',
    'propertyType', 'monthlyIncome', 'annualIncome', 'exp9', 'exp10', 'refered_By', 'createdBy', 'modifiedBy',
  ].map((k) => k.toLowerCase()),
);
const NUMBER_KEYS = new Set(
  [
    'total_Area', 'measured_Area', 'sqft_Rate', 'purchase_Value', 'total_Value', 'measured_Value', 'roundOff_Value', 'parking_Price',
    'all_In_Price', 'premium', 'premium_Percent', 'seller_Commission', 'seller_Commission_Percent', 'buyer_Commission',
    'buyer_Commission_Percent', 'seller_TransferFee', 'seller_TransferFee_Percent', 'buyer_TransferFee',
    'buyer_TransferFee_Percent', 'admin_Fees', 'admin_Fees_Percent', 'other_Fees', 'other_Fees_Percent', 'seller_Paid',
    'seller_Paid_Percent', 'developer_Due', 'developer_Due_Percent', 'rent_Per_Annum', 'short_Term_Rent', 'rent_Per_Day',
    'security_Deposit', 'advance_Deposit', 'tenant_Commission', 'landlord_Commission', 'maintenance_Fee', 'cmA_Range1',
    'cmA_Range2', 'installments', 'leaseAmount', 'portfolio', 'factor', 'common', 'cp', 'listingPriceUSD',
  ].map((k) => k.toLowerCase()),
);

const toBool = (v) => v === true || v === 1 || String(v).toLowerCase() === 'true';
const lowerFirst = (k) => k.charAt(0).toLowerCase() + k.slice(1);
const parseApiDate = (val) => {
  if (!val) return null;
  const d = new Date(val);
  return Number.isNaN(d.getTime()) ? null : d;
};
const blankIfZero = (v) => (v === undefined || v === null || String(v).trim() === '' || String(v).trim() === '0' ? '' : String(v));

const PmUnitDetailForm = ({ show, onHide, onSaved, initialRow, viewOnly = false }) => {
  const apiService = new LeaseApiService();
  const userName = useSelector(getName);
  const { currencyPrecision } = useCurrency();
  const decimals = currencyPrecision?.decimalNumbers || 2;

  const [propertyList, setPropertyList] = useState([]);
  const [bankList, setBankList] = useState([]);
  const [ownerList, setOwnerList] = useState([]);
  const [unitList, setUnitList] = useState([]);
  const [property, setProperty] = useState(null);
  const [unit, setUnit] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [rawUnit, setRawUnit] = useState(null);
  const [features, setFeatures] = useState([]);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loaded = !!rawUnit;

  const resetUnit = () => {
    setUnit(null);
    setRawUnit(null);
    setFeatures([]);
    setImages([]);
    setForm(emptyForm);
  };

  // Load all properties and all banks whenever the popup opens.
  useEffect(() => {
    if (!show) return;
    const loadDropdowns = async () => {
      try {
        const p = new URLSearchParams();
        p.append('dropdownTypes', 'UMProperties,UMBanks,UMSellers');
        const res = await apiService.getApiParamater('GET_UNIT_MULTIDROP', `?${p.toString()}`);
        setPropertyList(res?.data?.dropdowns?.UMProperties || []);
        setBankList(res?.data?.dropdowns?.UMBanks || []);
        setOwnerList(res?.data?.dropdowns?.UMSellers || []);
      } catch (error) {
        console.error('Error loading dropdowns', error);
        toast.error('Failed to load properties and banks');
      }
    };
    loadDropdowns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  // Opened from a listing row: preselect its property and unit once the dropdowns are ready.
  // Opened from the Add button: start empty.
  useEffect(() => {
    if (!show) return;
    if (!initialRow) {
      setProperty(null);
      setUnitList([]);
      resetUnit();
      return;
    }
    if (!propertyList.length || !bankList.length || !ownerList.length) return;
    const row = propertyList.find((p) => String(p.value) === String(initialRow.property_No));
    if (row) handlePropertyChange({ value: row, label: row.text }, initialRow.ref_No);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, initialRow, propertyList, bankList, ownerList]);

  const handlePropertyChange = async (selected, presetUnitRef) => {
    setProperty(selected);
    resetUnit();
    setUnitList([]);
    if (!selected) return;
    try {
      const params = new URLSearchParams();
      params.append('PageNo', 1);
      params.append('PageSize', 5000);
      params.append('PropertyNo', selected?.value?.value || '');
      const res = await apiService.getApiParamater('GET_UNIT_LIST', `?${params.toString()}`);
      const opts = (res?.data?.result || []).map((r) => ({ value: r.ref_No, label: r.unit_No }));
      setUnitList(opts);
      const preset = presetUnitRef ? opts.find((o) => o.value === presetUnitRef) : null;
      if (preset) await handleUnitChange(preset);
    } catch (error) {
      console.error('Error loading units', error);
      toast.error('Failed to load units');
    }
  };

  const handleUnitChange = async (selected) => {
    resetUnit();
    setUnit(selected);
    if (!selected) return;
    try {
      setLoading(true);
      const refNo = selected.value;
      const dp = new URLSearchParams();
      dp.append('Action', 'GetUnitDetailsByRefNo');
      dp.append('UnitRefNo', refNo);
      const unitRes = await apiService.getApiParamater('GET_UNIT_COMMON', `?${dp.toString()}`);
      const raw = unitRes?.data?.result;
      const ud = raw?.data !== undefined && typeof raw?.data === 'object' && !Array.isArray(raw.data) ? raw.data : raw && typeof raw === 'object' ? raw : {};

      const fp = new URLSearchParams();
      fp.append('Action', 'GetUnitFeatureByRefNo');
      fp.append('UnitRefNo', refNo);
      const fRes = await apiService.getApiParamater('GET_UNIT_COMMON', `?${fp.toString()}`);
      setFeatures(fRes?.data?.result?.data || []);

      const ip = new URLSearchParams();
      ip.append('Action', 'GetUnitImagesByRefNo');
      ip.append('UnitRefNo', refNo);
      const iRes = await apiService.getApiParamater('GET_UNIT_COMMON', `?${ip.toString()}`);
      const imgData = iRes?.data?.result?.data;
      setImages(Array.isArray(imgData) ? imgData : imgData?.Record_Id ? [imgData] : []);

      const bankText = blankIfZero(ud.RentBankName);
      const ownerId = blankIfZero(ud.Seller_Id);
      const ownerName = blankIfZero(ud.Seller_Name);
      const bankRow = bankText ? bankList.find((b) => String(b?.text).trim() === bankText.trim()) : null;
      setForm({
        refNo: String(ud.Ref_No ?? refNo),
        owner: ownerId || ownerName ? ownerList.find((o) => String(o.value) === ownerId) || { value: ownerId, text: ownerName } : null,
        unitClass: LEASE_CLASS_OPTIONS.find((o) => o.value === String(ud.Unit_Class ?? '').trim()) || null,
        rentPerMonth: blankIfZero(ud.Short_Term_Rent),
        maintFee: blankIfZero(ud.Maintenance_Fee),
        dueDate: parseApiDate(ud.Effeciency),
        accountName: blankIfZero(ud.RentPayableTo),
        iban: blankIfZero(ud.Street_Name),
        accountNo: blankIfZero(ud.RentBankAcNo),
        bankName: bankText ? (bankRow ? { value: bankRow, label: bankRow.text } : { value: { text: bankText, value: bankText }, label: bankText }) : null,
        bankAddress: blankIfZero(ud.SecDepPayableTo),
        swiftCode: blankIfZero(ud.ComPayableTo),
      });
      setRawUnit(ud);
    } catch (error) {
      console.error('Error loading unit', error);
      toast.error('Failed to load unit details');
    } finally {
      setLoading(false);
    }
  };

  const setField = (name, value) => setForm((prev) => ({ ...prev, [name]: value }));
  const handleInput = (e) => setField(e.target.name, e.target.value);
  const handleAmountBlur = (e) => {
    const { name, value } = e.target;
    const num = value === '' || isNaN(Number(value)) ? 0 : Number(value);
    setField(name, num.toFixed(decimals));
  };

  const handleSave = async () => {
    if (!loaded) {
      toast.warning('Select a property and unit first');
      return;
    }
    try {
      setSaving(true);
      // Start from the loaded record (PascalCase -> camelCase; the API binds case-insensitively),
      // dropping nulls so untouched columns keep their stored value.
      const unitMaster = {};
      Object.entries(rawUnit).forEach(([k, v]) => {
        if (v !== null && v !== undefined) unitMaster[lowerFirst(k)] = v;
      });
      BOOLEAN_KEYS.forEach((k) => {
        if (k in unitMaster) unitMaster[k] = toBool(unitMaster[k]);
      });
      Object.keys(unitMaster).forEach((k) => {
        const lk = k.toLowerCase();
        if (STRING_KEYS.has(lk)) unitMaster[k] = String(unitMaster[k]);
        else if (NUMBER_KEYS.has(lk)) unitMaster[k] = parseFloat(unitMaster[k]) || 0;
      });
      // The API only accepts ISO dates; loaded values can come back in other formats, so
      // re-serialise them and drop any that can't be parsed (the stored value is then kept).
      DATE_KEYS.forEach((k) => {
        if (!(k in unitMaster)) return;
        const d = parseApiDate(unitMaster[k]);
        if (d) unitMaster[k] = d.toISOString();
        else delete unitMaster[k];
      });
      Object.assign(unitMaster, {
        ref_No: form.refNo,
        seller_Id: form.owner?.value ? String(form.owner.value) : '0',
        seller_Name: form.owner?.text || '0',
        unit_Class: form.unitClass?.value || rawUnit.Unit_Class || '0',
        short_Term_Rent: parseFloat(form.rentPerMonth) || 0,
        maintenance_Fee: parseFloat(form.maintFee) || 0,
        ...(form.dueDate ? { effeciency: form.dueDate } : {}),
        rentPayableTo: form.accountName || '0',
        street_Name: form.iban || '0',
        rentBankAcNo: form.accountNo || '0',
        rentBankName: form.bankName?.label || '0',
        secDepPayableTo: form.bankAddress || '0',
        comPayableTo: form.swiftCode || '0',
        modifiedBy: userName || '0',
        modifiedOn: new Date(),
      });

      const unitFeatures = features.map((item) => ({
        status: item?.DescType || '0',
        description: item?.Description || '0',
        value: typeof item?.Value === 'boolean' ? item.Value : String(item?.Value).toLowerCase() === 'true',
        cnt: parseFloat(item?.Cnt) || 0,
        amount: parseFloat(item?.Amount) || 0,
        isNewInEdit: item?.IsNewInEdit === 0 ? false : true,
      }));
      const unitImages = images.map((item) => ({
        record_Id: 0,
        image_Description: '0',
        image_Filename: item.Image_Filename || '0',
        isDefault: true,
        folder: item.Folder || '0',
        url: item.Url || '0',
        isAdded: false,
        isUpdated: false,
        isDeleted: false,
      }));

      const body = {
        unitMasterMain: {
          unitMaster,
          unitFeatures,
          unitImages,
          unitPayables: [{ description: '0', value: true, landlord: true, tenant: true }],
          unitYearlyRents: [
            {
              year: 0,
              rentPerSqftRest: 0,
              rentPerSqftKitchen: 0,
              rentPerSqftTerrace: 0,
              totalRent: 0,
              scPerSqftRest: 0,
              scPerSqftKitchen: 0,
              scPerSqftTerrace: 0,
              totalServiceCharge: 0,
              chilledWaterPerSqftRest: 0,
              chilledWaterPerSqftKitchen: 0,
              chilledWaterPerSqftTerrace: 0,
              totalChilledWaterCharge: 0,
            },
          ],
          unitFixedCharges: [{ description: '0', amount: 0 }],
        },
      };
      const putRes = await apiService.put('UPDATE_UNIT', body);
      if (putRes?.data?.statusCode === 200 || putRes?.data?.statusCode === 201) {
        toast.success(putRes?.data?.message || 'Saved successfully');
        onSaved?.();
        onHide?.();
      } else {
        toast.warning(`${putRes?.data?.message}`);
      }
    } catch (error) {
      console.error('Error Save Data', error);
      toast.error('Failed to save PM unit details');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (!saving) onHide?.();
  };

  // Owners list can run to tens of thousands of rows, so search/paginate client-side like unit master.
  const searchableOwners = useMemo(
    () => ownerList.map((o) => ({ ...o, _v: String(o?.value ?? '').toLowerCase(), _t: String(o?.text ?? '').toLowerCase() })),
    [ownerList],
  );
  const loadOwners = (search, loadedOptions, { page }) => {
    const term = (search || '').toLowerCase();
    const filtered = term ? searchableOwners.filter((o) => o._v.includes(term) || o._t.includes(term)) : searchableOwners;
    const start = page * 100;
    return Promise.resolve({
      options: filtered.slice(start, start + 100),
      hasMore: start + 100 < filtered.length,
      additional: { page: page + 1 },
    });
  };
  const ownerSelect = (by) => (
    <AsyncPaginate
      key={`${by}-${ownerList.length}`}
      menuPortalTarget={document.body}
      styles={selectStyles}
      loadOptions={(s, l, a) => loadOwners(s, l, a).then((r) => ({ ...r, options: r.options.map((o) => ({ value: o, label: by === 'name' ? o.text : String(o.value) })) }))}
      debounceTimeout={300}
      defaultOptions
      additional={{ page: 0 }}
      value={form.owner ? { value: form.owner, label: by === 'name' ? form.owner.text : String(form.owner.value) } : null}
      onChange={(opt) => setField('owner', opt?.value || null)}
      isDisabled={!loaded || viewOnly}
      isClearable
    />
  );

  const row = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '20px', marginBottom: '16px' };
  const field = (label, control) => (
    <div className="d-flex align-items-center gap-2">
      <label className="form-label fw-bold mb-0" style={{ minWidth: '120px' }}>
        {label}
      </label>
      <div className="w-100">{control}</div>
    </div>
  );
  const readOnly = (value) => <input type="text" className="p-2 form-control" value={value} disabled />;
  const text = (name, extra = {}) => <input type="text" className="p-2 form-control" name={name} value={form[name]} onChange={handleInput} disabled={!loaded || viewOnly} {...extra} />;

  return (
    <Modal show={show} onHide={handleCancel} size="xl" centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title as="h6">{viewOnly ? 'View PM Unit Details' : 'PM Unit Details'}</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ position: 'relative' }}>
        {loading && (
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 }}>
            <div className="spinner-border text-primary" role="status" />
          </div>
        )}
        <div style={row}>
          {field(
            'Property',
            <Select
              menuPortalTarget={document.body}
              styles={selectStyles}
              value={property}
              onChange={(opt) => handlePropertyChange(opt)}
              options={propertyList.map((p) => ({ value: p, label: p?.text }))}
              isClearable
              isDisabled={viewOnly}
            />,
          )}
          {field('Unit No', <Select menuPortalTarget={document.body} styles={selectStyles} value={unit} onChange={handleUnitChange} options={unitList} isDisabled={!property || viewOnly} isClearable />)}
          {field('Unit RefNo', readOnly(form.refNo))}
        </div>
        <div style={row}>
          {field('Owner Name', ownerSelect('name'))}
          {field('Owner Id', ownerSelect('id'))}
          {field(
            'Lease Class',
            <Select menuPortalTarget={document.body} styles={selectStyles} value={form.unitClass} onChange={(opt) => setField('unitClass', opt)} options={LEASE_CLASS_OPTIONS} isDisabled={!loaded || viewOnly} />,
          )}
        </div>
        <div style={row}>
          {field('Rent Per Month', text('rentPerMonth', { onBlur: handleAmountBlur }))}
          {field('Maint. Fee Amt', text('maintFee', { onBlur: handleAmountBlur }))}
          {field(
            'Maint. Due Date',
            <DatePicker
              todayButton="Today"
              selected={form.dueDate}
              onChange={(date) => setField('dueDate', date)}
              dateFormat="dd-MMM-yyyy"
              className="p-2 form-control w-100"
              disabled={!loaded || viewOnly}
            />,
          )}
        </div>
        <div style={row}>
          {field('Account Name', text('accountName'))}
          {field('IBAN No', text('iban'))}
          {field('Acct No', text('accountNo'))}
        </div>
        <div style={row}>
          {field(
            'Bank Name',
            <Select
              menuPortalTarget={document.body}
              styles={selectStyles}
              value={form.bankName}
              onChange={(opt) => setField('bankName', opt)}
              options={bankList.map((b) => ({ value: b, label: b?.text }))}
              isDisabled={!loaded || viewOnly}
            />,
          )}
          {field('Bank Address', <textarea className="p-2 form-control" rows={2} name="bankAddress" value={form.bankAddress} onChange={handleInput} disabled={!loaded || viewOnly} />)}
          {field('Swift Code', text('swiftCode'))}
        </div>
      </Modal.Body>
      <Modal.Footer className="justify-content-start gap-3">
        {!viewOnly && (
          <button type="button" className="btn btn-success px-4" onClick={handleSave} disabled={saving || !loaded}>
            {saving ? <ClipLoader color="#ffffff" size={16} /> : 'Save'}
          </button>
        )}
        <button type="button" className="btn btn-info text-white px-4" onClick={handleCancel} disabled={saving}>
          {viewOnly ? 'Close' : 'Cancel'}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default PmUnitDetailForm;
